import React, { useState } from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Button, H1, ListRow, RadioGroup, Text, VStack } from '@betterment/samba-react';
import { useNavigate } from 'react-router-dom';

import { FlowLayout } from './FlowLayout';
import { useDistributionFlow } from './hooks';

import { AccountIcon } from '../Icon/AccountIcon';

import type { BankAccount } from '../Shared/data';

// Mock data — replace with real API data when wired up
const MOCK_LINKED_ACCOUNTS: BankAccount[] = [
  {
    id: 1,
    type: 'bank_account',
    displayName: 'WF Checking ••••1234',
    balanceCents: 504929,
    isLocked: false,
    isPendingManualLink: false,
    isBettermentChecking: false,
    icon: {
      lightUrl: 'https://cdn.betterment.com/institution_icons/wells_fargo_light.png',
      darkUrl: 'https://cdn.betterment.com/institution_icons/wells_fargo_dark.png',
    },
  },
  {
    id: 2,
    type: 'bank_account',
    displayName: 'BofA Savings ••••1234',
    balanceCents: 504929,
    isLocked: false,
    isPendingManualLink: false,
    isBettermentChecking: false,
    icon: {
      lightUrl: 'https://cdn.betterment.com/institution_icons/bank_of_america_light.png',
      darkUrl: 'https://cdn.betterment.com/institution_icons/bank_of_america_dark.png',
    },
  },
];

const BankAccountSelection: React.FC = () => {
  const navigate = useNavigate();
  const { nextStep } = useDistributionFlow();
  const [selectedId, setSelectedId] = useState<string>('');

  const handleContinue = () => {
    if (nextStep) void navigate(`../${nextStep}`, { relative: 'path' });
  };

  return (
    <FlowLayout gap="space.600">
      <VStack css={{ gap: 'space.500' }}>
        <VStack css={{ gap: 'space.200' }}>
          <H1 variant="title400">Select an account.</H1>
          <Text variant="text100">The following connected accounts are eligible funding sources.</Text>
        </VStack>

        <RadioGroup appearance="default" value={selectedId || undefined} onValueChange={setSelectedId}>
          <>
            {MOCK_LINKED_ACCOUNTS.map(account => (
              <RadioGroup.ItemContainer key={account.id}>
                <RadioGroup.Item id={String(account.id)} value={String(account.id)} aria-label={account.displayName} />
                <RadioGroup.ItemLabel htmlFor={String(account.id)}>
                  <ListRow css={{ padding: 0 }}>
                    <ListRow.LeadingAsset css={{ width: '36px', height: '36px' }}>
                      <AccountIcon asset={account.icon} name={account.displayName} />
                    </ListRow.LeadingAsset>
                    <ListRow.LeadingContent>
                      <Text variant="text100">{account.displayName}</Text>
                      {account.balanceCents != null && (
                        <Text variant="text50" color="text.weak">
                          Checking – {formatMoney(toMoney(account.balanceCents))}
                        </Text>
                      )}
                    </ListRow.LeadingContent>
                  </ListRow>
                </RadioGroup.ItemLabel>
              </RadioGroup.ItemContainer>
            ))}

            <RadioGroup.ItemContainer>
              <RadioGroup.Item id="connect_new" value="connect_new" aria-label="Connect a new institution" />
              <RadioGroup.ItemLabel htmlFor="connect_new">
                <Text variant="text100">Connect a new institution</Text>
              </RadioGroup.ItemLabel>
            </RadioGroup.ItemContainer>
          </>
        </RadioGroup>
      </VStack>

      <Button
        variant="solidPrimary"
        css={{ width: 'fit-content' }}
        disabled={!selectedId}
        onClick={() => void handleContinue()}
      >
        Continue
      </Button>
    </FlowLayout>
  );
};

export default BankAccountSelection;
